/**
 * AIAssistant.tsx — Right-rail project assistant ("Assistente CME").
 *
 * The prototype's canned-reply chat was removed on 2026-07-24 (no fabricated AI
 * answers next to real data). It became REAL on 2026-07-29, backed by
 * tender-api `POST /v1/projects/{id}/assistant`. Since the 29 Sep 2026 build
 * round the web chat STARTS an answer with `POST /v1/projects/{id}/assistant/runs`
 * and polls `GET …/assistant/runs/{runId}` for its steps and its answer, so an
 * answer is no longer bounded by the platform's ~60 s cut on one request; the
 * single request still exists on the server with its old limits.
 *
 * The rail is live on any screen that publishes an active project — today Audit
 * & Discrepanze and Strategia & Margini (see lib/activeProject). Everywhere else
 * it keeps the honest "In arrivo" panel rather than pretending to have a project
 * in scope. Opening suggestions follow the stage; the data behind them does not.
 *
 * Read-only by design: it answers questions, it never edits margins or data.
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { Markdown } from "../Markdown";
import { I } from "../icons";
import { pdfPageLabel, useSourceOpener } from "../../lib/sourceOpener";
import type { AssistantSource, AssistantStepView } from "../../lib/tenderApi";
import { useActiveProject, type ActiveProject } from "../../lib/activeProject";
import { useAssistantThread } from "../../lib/assistantThread";
import type { AssistantContext } from "../../lib/tenderApi";
import { contextLabel } from "../../lib/nav";

/** Kept in step with AssistantAsk.question (api/schemas.py, max_length=2000):
 *  the client refuses what the server would reject, instead of spending a round
 *  trip to be told. */
const MAX_QUESTION = 2000;

/**
 * Opening questions, chosen for the screen the user is on. The assistant sees
 * the same project data either way — only the prompts differ, so a user landing
 * on Strategia is offered margin questions rather than categorisation ones.
 */
const SUGGESTIONS: Record<string, string[]> = {
  audit: [
    "Quali categorie pesano di più sul totale?",
    "Quante discrepanze critiche ci sono e su quali voci?",
    "Quali sono le voci di costo più grandi?",
  ],
  strategy: [
    "Su quali categorie ho il margine maggiore?",
    "Per quante categorie non ho ancora inserito una decisione make/buy?",
    "Il margine previsto è sopra o sotto l'obiettivo?",
  ],
};

const DEFAULT_SUGGESTIONS = SUGGESTIONS.audit;

/**
 * What the answer could see of the project's DOCUMENTS, in one sentence.
 *
 * `P-68`. The four states are deliberately four different sentences rather than
 * a count that happens to be zero: «searched and found nothing» and «could not
 * search» produce identical numbers and mean opposite things, and the second one
 * printed as the first is a confident answer with half its evidence missing and
 * nothing on screen to say so. Omar asked for this disclosure by name
 * (2026-09-11).
 *
 * Returns `null` only for `off` — an environment with no Knowledge Base at all,
 * where there is nothing to disclose because there is nothing to miss.
 */
function knowledgeNote(ctx: AssistantContext): { text: string; muted: boolean } | null {
  switch (ctx.kbStatus) {
    case "ok": {
      const parts: string[] = [];
      if (ctx.kbClaims) parts.push(`${ctx.kbClaims} fatti riconosciuti`);
      if (ctx.kbDocuments) parts.push(`${ctx.kbDocuments} estratti dai documenti`);
      if (ctx.kbSyntheses) parts.push(`${ctx.kbSyntheses} dalle sintesi`);
      // The lens split counts PASSAGES only — the server derives it from the
      // passage list, not the facts (review S29). Saying «estratti» inside the
      // parenthesis keeps the arithmetic honest: with facts present the two
      // numbers legitimately do not add up to the total above.
      const lenses: string[] = [];
      if (ctx.kbProjectSources) lenses.push(`${ctx.kbProjectSources} estratti del progetto`);
      if (ctx.kbCompanySources) lenses.push(`${ctx.kbCompanySources} dalla conoscenza aziendale`);
      return {
        text: `Anche ${
          parts.length > 2
            ? `${parts.slice(0, -1).join(", ")} e ${parts[parts.length - 1]}`
            : parts.join(" e ")
        }${lenses.length ? ` (${lenses.join(", ")})` : ""}.`,
        muted: true,
      };
    }
    case "empty":
      return {
        text: "Nessun documento pertinente trovato per questa domanda: risposta basata sul solo computo.",
        muted: true,
      };
    case "unavailable":
      return {
        text: "Knowledge Base non raggiungibile: risposta basata sul solo computo, senza i documenti del progetto.",
        muted: false,
      };
    case "partial":
      // **`FE-5`.** This case used to render the sentence above, which says in
      // so many words that NO project document contributed — beside a source
      // list showing the pages that did. Half or more of the lookups failing is
      // a real degradation and must still be disclosed; it is not the same
      // event as the Knowledge Base being unreachable, and the reader can see
      // that it is not, because the chips are right there.
      return {
        text: `Alcune ricerche non sono riuscite: la risposta usa il computo e ${
          ctx.kbDocuments === 1
            ? "1 estratto dai documenti"
            : `${ctx.kbDocuments} estratti dai documenti`
        }, ma parte dei documenti non è stata consultata.`,
        muted: false,
      };
    case "no_access":
      return {
        text: "Accesso alla Knowledge Base non disponibile: risposta basata sul solo computo. Apri «Esplora KB» una volta per autorizzarlo.",
        muted: false,
      };
    default:
      return null;
  }
}

/**
 * Says which lists the answer only saw part of. A capped list that does not
 * announce itself reads as a complete one — this is the user-facing half of the
 * backend's cap-reporting contract.
 */
function truncationNote(ctx: AssistantContext): string {
  const parts: string[] = [];
  if (ctx.lineItems > ctx.topItemsShown) parts.push(`le ${ctx.topItemsShown} voci maggiori`);
  if (ctx.categories > ctx.categoriesShown)
    parts.push(`le ${ctx.categoriesShown} categorie maggiori`);
  if (ctx.discrepancies > ctx.discrepanciesShown)
    parts.push(`le ${ctx.discrepanciesShown} discrepanze maggiori`);
  return parts.length ? ` (dettaglio su ${parts.join(", ")})` : "";
}

interface AIAssistantProps {
  context: string;
  visible: boolean;
  onToggle: () => void;
}

export function AIAssistant({ context, visible, onToggle }: AIAssistantProps): JSX.Element {
  const project = useActiveProject();

  if (!visible) {
    return (
      <button
        onClick={onToggle}
        title={project ? `Assistente CME — ${project.name}` : "Assistente AI — in arrivo"}
        style={{
          position: "absolute",
          right: 14,
          bottom: 14,
          zIndex: 20,
          appearance: "none",
          border: "1px solid var(--c-line-strong)",
          cursor: "pointer",
          background: "var(--c-bg-elev)",
          color: "var(--c-ai)",
          padding: "8px 12px",
          borderRadius: 999,
          fontSize: 12.5,
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: 8,
          boxShadow: "var(--sh-2)",
        }}
      >
        <I.Sparkle size={12} />
        Assistente AI
      </button>
    );
  }

  return (
    <aside
      style={{
        width: 320,
        flexShrink: 0,
        borderLeft: "1px solid var(--c-line)",
        background: "var(--c-bg-elev)",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <Header subtitle={project ? project.name : contextLabel(context)} onToggle={onToggle} />
      {project ? (
        // Keyed by project: switching tender gives the panel a clean slate
        // rather than leaving the previous project's draft in the box.
        <ProjectChat key={project.id} project={project} context={context} />
      ) : (
        <NoProjectBody />
      )}
    </aside>
  );
}

function Header({ subtitle, onToggle }: { subtitle: string; onToggle: () => void }): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 14px",
        borderBottom: "1px solid var(--c-line)",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: 6,
          flexShrink: 0,
          background: "var(--c-ai-soft)",
          color: "var(--c-ai)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <I.Sparkle size={14} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--c-ink)" }}>Assistente CME</div>
        <div
          style={{
            fontSize: 11,
            color: "var(--c-ink-3)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          Contesto: <span style={{ color: "var(--c-ai)", fontWeight: 500 }}>{subtitle}</span>
        </div>
      </div>
      <button className="btn btn-ghost btn-icon btn-sm" title="Chiudi" onClick={onToggle}>
        <I.X size={13} />
      </button>
    </div>
  );
}

/**
 * Shown when the rail is open on Audit or Strategia and no project resolves —
 * an empty workspace, or a failed projects query.
 *
 * `P-66` (2026-09-11) removed the OTHER case this used to serve. It previously
 * branched on `context`, because the rail was mounted on every screen and most
 * of them could never answer, so it also had to say "In arrivo" and point at
 * Audit. Since the rail is now hidden everywhere except Audit and Strategia
 * (`nav.ts` `hideAI`), `context` is always one of those two and that branch was
 * unreachable. It is deleted rather than left as a dead alternative: a branch
 * nobody can reach is a claim nobody can check.
 */
function NoProjectBody(): JSX.Element {
  const navigate = useNavigate();
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 12,
        padding: "24px 22px",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: "var(--c-ai-soft)",
          color: "var(--c-ai)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <I.Sparkle size={18} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--c-ink)" }}>
          Assistente di progetto
        </span>
      </div>
      <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--c-ink-2)" }}>
        Nessun progetto da analizzare. L&apos;assistente si attiva appena un progetto è disponibile
        su questa schermata.
      </p>
      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.6, color: "var(--c-ink-3)" }}>
        Per domande sulla conoscenza aziendale c&apos;è la chat della Knowledge Base:
      </p>
      <button className="btn btn-sm" onClick={() => navigate("/kb/explore")}>
        <I.Network size={13} /> Apri Esplora KB
      </button>
    </div>
  );
}

function ProjectChat({
  project,
  context,
}: {
  project: ActiveProject;
  context: string;
}): JSX.Element {
  // Transcript AND in-flight state come from the shared store above the rail, so
  // both survive collapsing the panel or leaving the page (lib/assistantThread).
  const { messages, pending, progress, ask, draft, setDraft } = useAssistantThread(project.id);
  // The draft lives in the shared store, not here: this component is
  // unmounted every time the rail is collapsed.
  const input = draft;
  const setInput = setDraft;
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Follow the conversation as it grows, including while a reply is pending.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    // …and as each step of an answer being built arrives (29 Sep build round).
  }, [messages.length, pending, progress?.steps.length]);

  function send(text?: string): void {
    const question = (text ?? input).trim();
    // One question at a time: the transcript is append-only, so two in-flight
    // answers could land against the wrong questions. The store enforces this
    // too — this check only avoids clearing the input on a send that won't fire.
    if (!question || pending) return;
    if (question.length > MAX_QUESTION) return;
    setInput("");
    ask(question);
  }

  const tooLong = input.trim().length > MAX_QUESTION;

  return (
    <>
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "14px 14px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        {messages.length === 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--c-ink-2)" }}>
              Domande su <strong>{project.name}</strong> — categorie, voci di costo, discrepanze,
              margini. Legge anche i documenti caricati per questo progetto, le loro sintesi e la
              conoscenza aziendale condivisa, e indica sempre da dove viene ogni informazione.
            </p>
            {(SUGGESTIONS[context] ?? DEFAULT_SUGGESTIONS).map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                style={{
                  appearance: "none",
                  textAlign: "left",
                  cursor: "pointer",
                  border: "1px solid var(--c-line)",
                  background: "var(--c-bg)",
                  color: "var(--c-ink-2)",
                  borderRadius: 8,
                  padding: "7px 10px",
                  fontFamily: "inherit",
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                {s}
              </button>
            ))}
            {/* Says what it CANNOT see. The previous copy claimed it had no
                access to margins, which stopped being true when the Strategia
                table joined the pack — and it sat directly under three margin
                suggestion buttons. */}
            <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.55, color: "var(--c-ink-3)" }}>
              Non vede i singoli preventivi né l&apos;anagrafica fornitori — quelli sono su
              Fornitori &amp; Storico, e non vede i documenti di altre gare. Risponde soltanto: non
              modifica dati.
            </p>
          </div>
        )}

        {messages.map((m, i) =>
          m.role === "user" ? (
            <div key={i} style={{ display: "flex", justifyContent: "flex-end" }}>
              <div
                style={{
                  maxWidth: "88%",
                  padding: "8px 11px",
                  background: "var(--c-brand)",
                  color: "var(--c-ink-inv)",
                  borderRadius: "12px 12px 2px 12px",
                  fontSize: 12.5,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {m.content}
              </div>
            </div>
          ) : (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span className="ai-badge" style={{ alignSelf: "flex-start" }}>
                <I.Sparkle size={9} /> AI
              </span>
              <div style={{ color: m.error ? "var(--c-err)" : "var(--c-ink)", fontSize: 12.5 }}>
                {m.error ? m.content : <Markdown variant="chat">{m.content}</Markdown>}
              </div>
              {m.context && (
                <div style={{ fontSize: 10.5, color: "var(--c-ink-3)", lineHeight: 1.5 }}>
                  Basato su {m.context.categories} categorie · {m.context.lineItems} voci ·{" "}
                  {m.context.discrepancies} discrepanze
                  {truncationNote(m.context)}
                  {(() => {
                    const note = knowledgeNote(m.context!);
                    if (!note) return null;
                    return (
                      <div
                        data-testid="kb-note"
                        style={{ color: note.muted ? undefined : "var(--c-warn)", marginTop: 2 }}
                      >
                        {note.text}
                      </div>
                    );
                  })()}
                </div>
              )}
              <AnswerSources sources={m.sources} citationMode={m.citationMode} index={i} />
            </div>
          ),
        )}

        {pending && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "var(--c-ink-3)",
              fontSize: 12,
            }}
          >
            <span className="ai-badge">
              <I.Sparkle size={9} /> AI
            </span>
            <span>Sto leggendo i dati del progetto…</span>
          </div>
        )}
        {/* 29 Sep build round: an answer may now work for up to five minutes, so
            the reader sees what it is doing — each lookup as it completes, with
            the pages it opened — instead of one unchanging line. */}
        {pending && progress && progress.steps.length > 0 && (
          <ProgressSteps elapsedS={progress.elapsedS} steps={progress.steps} />
        )}
      </div>

      <div
        style={{
          borderTop: "1px solid var(--c-line)",
          padding: 10,
          display: "flex",
          flexDirection: "column",
          gap: 6,
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
          <textarea
            className="input"
            rows={2}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter breaks the line — the convention the KB
              // chat already uses.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder="Fai una domanda sul progetto…"
            aria-label="Domanda per l'assistente"
            style={{
              flex: 1,
              resize: "none",
              fontFamily: "inherit",
              fontSize: 12.5,
              lineHeight: 1.5,
              padding: "7px 9px",
              height: "auto",
            }}
          />
          <button
            className="btn btn-sm btn-primary"
            onClick={() => send()}
            disabled={pending || !input.trim() || tooLong}
            title="Invia"
          >
            <I.Send size={13} />
          </button>
        </div>
        {tooLong && (
          <div style={{ fontSize: 10.5, color: "var(--c-err)" }}>
            Domanda troppo lunga ({input.trim().length} caratteri, massimo {MAX_QUESTION}).
          </div>
        )}
      </div>
    </>
  );
}

/**
 * The evidence under an answer, as chips the reader can open — `KBR-74`.
 *
 * Until now «Assistente CME» named its sources only INSIDE the answer text,
 * because prose is all a model can emit. «Esplora KB» beside it has had
 * clickable citations since `KBR-68`, so the same reader got two different
 * answers to «show me that page» depending on which screen they were on.
 *
 * A synthesis passage opens «Esplora KB» rather than a PDF: a page this system
 * wrote has no original file of its own, and sending the reader to a PDF for it
 * would present a summary with the authority of a source document.
 */
function AnswerSources({
  sources,
  citationMode,
  index,
}: {
  sources?: AssistantSource[];
  citationMode?: "markers" | "pages";
  index: number;
}): JSX.Element | null {
  const { open: openSource, error, busy } = useSourceOpener();
  const navigate = useNavigate();
  if (!sources || sources.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 2 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {sources.map((src, j) => {
          const key = `${index}:${j}`;
          const page = pdfPageLabel(src.pageStart, src.pageEnd);
          const where = src.scope === "project" ? "progetto" : "conoscenza aziendale";
          return (
            <button
              key={key}
              data-testid={src.pageId ? "assistant-source-page" : "assistant-source-document"}
              disabled={busy === key}
              onClick={() =>
                src.pageId
                  ? navigate("/kb/explore")
                  : openSource(
                      {
                        documentId: src.documentId,
                        documentTitle: src.title,
                        pageStart: src.pageStart,
                      },
                      key,
                    )
              }
              title={
                src.pageId
                  ? `Sintesi (${where}): ${src.title} — apre «Esplora KB»`
                  : `Fonte (${where}): ${src.title}${page ? `, ${page}` : ""} — apre il file originale`
              }
              style={{
                appearance: "none",
                cursor: busy === key ? "progress" : "pointer",
                border: "1px solid var(--c-line)",
                background: "var(--c-bg-elev)",
                color: "var(--c-ink-2)",
                borderRadius: 6,
                fontSize: 10.5,
                padding: "2px 6px",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {src.evidenceIds && src.evidenceIds.length > 0
                ? `[${src.evidenceIds.join(", ")}] `
                : ""}
              {src.pageId ? "Sintesi" : "Documento"} · {src.title}
              {page ? `, ${page}` : ""}
            </button>
          );
        })}
      </div>
      <div data-testid="assistant-citation-mode" style={{ fontSize: 10, color: "var(--c-ink-3)" }}>
        {/* **`FE-3`.** Without this line the two states look identical: an
            answer whose own `[E#]` references were resolved, and an answer that
            wrote none at all, where the chips are simply every page that was
            read. The second is the weaker claim and was being shown with the
            authority of the first. Neither says the page SUPPORTS the sentence —
            nothing here evaluates that, so the wording stops where the evidence
            does. */}
        {citationMode === "markers"
          ? "Riferimenti citati nella risposta e risolti sulla pagina indicata. Il riferimento dice da dove viene la frase, non che la pagina la dimostri."
          : "Pagine consultate per questa risposta. La risposta non contiene riferimenti puntuali: queste pagine sono state lette, non collegate a singole frasi."}
      </div>
      {error && error.key.startsWith(`${index}:`) && (
        <div
          data-testid="assistant-source-error"
          style={{ fontSize: 10.5, color: "var(--c-warn)" }}
        >
          {error.text}
        </div>
      )}
    </div>
  );
}

/** The steps of an answer still being built — what the assistant has looked up
 *  so far and which pages it opened. Rendered only while the answer is pending. */
function ProgressSteps({
  elapsedS,
  steps,
}: {
  elapsedS: number;
  steps: AssistantStepView[];
}): JSX.Element {
  return (
    <div
      data-testid="assistant-progress"
      style={{ margin: "4px 0 0 30px", color: "var(--c-ink-3)", fontSize: 12 }}
    >
      <div>
        Sto cercando nei documenti — {steps.length} {steps.length === 1 ? "passaggio" : "passaggi"},{" "}
        {Math.round(elapsedS)} s
      </div>
      <ol
        aria-label="Passaggi della risposta in corso"
        style={{ margin: "4px 0 0 16px", padding: 0 }}
      >
        {steps.map((step) => (
          <li key={step.index} data-testid="assistant-progress-step">
            {step.summary}
            {step.pages.length > 0 &&
              ` · ${step.pages
                .slice(0, 3)
                .map((pg) => `«${pg.documentTitle}» p. ${pg.page ?? "?"}`)
                .join(", ")}${step.pages.length > 3 ? ` e altre ${step.pages.length - 3}` : ""}`}
            {step.isError && " — non riuscito"}
          </li>
        ))}
      </ol>
    </div>
  );
}
